import "@taxbit/react-sdk/style/inline.css";
import "./taxbit-theme.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// NOTE: this import only resolves to OUR React 17 because of patches/react+17.0.2.patch
// (adds an `exports` map so ESM deps can resolve `react/jsx-runtime` locally instead of
// walking up to the monorepo root's React 19). Removable once frontend is on React >= 18.
import { TaxbitQuestionnaire } from "@taxbit/react-sdk";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import ContainedButton from "src/components/common/ContainedButton";
import {
  BORDER_LIGHT,
  Card,
  NAVY,
  SALMON,
  StatusPill,
  TEXT_SECONDARY,
  PillTone,
} from "./styles";
import { buildTaxDocumentationPrefill } from "./taxDocumentationPrefill";
import { formatUtcDate } from "./utils";

type DocStatus = "NOT_STARTED" | "PENDING" | "VALID" | "ACTION_REQUIRED";

// "VALID" is the only state that counts as done; "PENDING" means submitted-but-validating.
const STATUS_META: Record<
  DocStatus,
  { label: string; tone: PillTone; blurb: string; rowValue: string; cta?: string }
> = {
  NOT_STARTED: {
    label: "Action required",
    tone: "warning",
    blurb:
      "Complete your W-9/W-8 tax form so we can report your crypto activity to the IRS accurately.",
    rowValue: "Not started",
    cta: "Start tax form",
  },
  PENDING: {
    label: "Validating",
    tone: "neutral",
    blurb:
      "Your tax form was submitted and is being validated with the IRS. This can take a little while — no action needed right now.",
    rowValue: "Validating",
  },
  VALID: {
    label: "Form valid",
    tone: "success",
    blurb: "Your US taxpayer certification is on file. Update it if your circumstances change.",
    rowValue: "On file",
    cta: "View / update form",
  },
  ACTION_REQUIRED: {
    label: "Action required",
    tone: "error",
    blurb:
      "There was a problem with your submitted tax info (e.g. a TIN mismatch). Please review and re-submit.",
    rowValue: "Needs attention",
    cta: "Fix tax form",
  },
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: NAVY }}>{value}</Typography>
  </Stack>
);

const TaxDocumentationWidget = () => {
  const dispatch = useAppDispatch();
  const statusQuery = api.useGetTaxDocumentationStatusQuery();
  const [mintToken, tokenState] = api.useLazyGetTaxDocumentationTokenQuery();
  const { details, address } = useUserSlice();
  const prefill = useMemo(
    () => buildTaxDocumentationPrefill(details, address),
    [details, address],
  );

  const [open, setOpen] = useState(false);
  const [bearerToken, setBearerToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [sdkAuthFailed, setSdkAuthFailed] = useState(false);
  // A genuine token expiry needs exactly one re-mint; more means TaxBit is rejecting fresh
  // tokens, and retrying further would loop forever.
  const remintAttempts = useRef(0);

  const status: DocStatus = statusQuery.data?.status ?? "NOT_STARTED";
  const meta = STATUS_META[status];
  // ACTION_REQUIRED → re-open the form pre-filled but editable so the user can correct it.
  // Other openable statuses (NOT_STARTED, VALID) skip already-valid fields for a faster
  // re-confirm/first-fill flow.
  const adaptiveMode = status === "ACTION_REQUIRED" ? "full" : "skipEdit";

  const fetchToken = useCallback(async () => {
    const result = await mintToken().unwrap();
    setBearerToken(result.access_token);
  }, [mintToken]);

  const handleOpen = async () => {
    setSubmitted(false);
    setSdkAuthFailed(false);
    remintAttempts.current = 0;
    setOpen(true);
    try {
      await fetchToken();
    } catch {
      // tokenState.isError surfaces the failure in the dialog body
    }
  };

  const handleClose = () => {
    setOpen(false);
    setBearerToken(null);
    // Pull the latest status — it may now be PENDING/VALID after a submission.
    void statusQuery.refetch();
    // Opening the form provisions the TaxBit account, so a cached 404 on it is now stale.
    dispatch(api.util.invalidateTags(["TaxAccount"]));
  };

  // Re-mint once on a 401. If the fresh token is also rejected it's configuration, not expiry.
  const handleSdkError = useCallback(
    async (error: Error) => {
      if (/401|403|unauthor|forbidden/i.test(error?.message ?? "")) {
        if (remintAttempts.current >= 1) {
          setBearerToken(null);
          setSdkAuthFailed(true);
          return;
        }
        remintAttempts.current += 1;
        setBearerToken(null);
        await fetchToken().catch(() => undefined);
      }
    },
    [fetchToken],
  );

  const handleSuccess = useCallback(() => {
    // onSuccess only means TaxBit received it, not that the TIN matched — the webhook and a
    // refetch resolve the true state.
    setSubmitted(true);
    void statusQuery.refetch();
    dispatch(api.util.invalidateTags(["TaxAccount"]));
  }, [statusQuery, dispatch]);

  useEffect(() => {
    if (!open) return;
    return () => setSubmitted(false);
  }, [open]);

  return (
    <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.75, height: "100%" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
        <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
          Tax documentation
        </Typography>
        {statusQuery.isLoading ? (
          <Skeleton variant="rounded" width={88} height={24} />
        ) : (
          <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
        )}
      </Stack>

      {statusQuery.isLoading ? (
        <Skeleton variant="rounded" height={96} />
      ) : (
        <>
          <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
            {meta.blurb}
          </Typography>
          {statusQuery.isError && (
            <Typography sx={{ fontSize: 12, color: "error.main" }}>
              Unable to load your tax documentation status right now.
            </Typography>
          )}
          <Stack spacing={1.25} sx={{ mt: "auto", pt: 1.75, borderTop: `1px solid ${BORDER_LIGHT}` }}>
            <Row label="Form W-9 / W-8" value={meta.rowValue} />
            <Row
              label="Last updated"
              value={
                statusQuery.data?.lastSyncedAt ? formatUtcDate(statusQuery.data.lastSyncedAt) : "—"
              }
            />
            {meta.cta &&
              (status === "VALID" ? (
                <Stack direction="row">
                  <ButtonBase
                    onClick={() => void handleOpen()}
                    sx={{
                      fontFamily: "Roobert, sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      color: SALMON,
                      "&:hover": { color: NAVY },
                    }}>
                    {meta.cta}
                  </ButtonBase>
                </Stack>
              ) : (
                <Stack direction="row" sx={{ pt: 0.75 }}>
                  <ContainedButton
                    color="salmon"
                    width="160px"
                    height="38px"
                    onClick={() => void handleOpen()}>
                    {meta.cta}
                  </ContainedButton>
                </Stack>
              ))}
          </Stack>
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Tax documentation</DialogTitle>
        <DialogContent dividers>
          {submitted ? (
            <Stack spacing={1.5} alignItems="center" py={4}>
              <CheckCircleOutlineRoundedIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Submission received
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Thanks — your form is being validated with the IRS. We'll update your status here
                once it completes.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Done
              </Button>
            </Stack>
          ) : sdkAuthFailed ? (
            <Stack spacing={1.5} py={4} alignItems="center">
              <Typography variant="body2" color="error.main">
                The tax form service rejected our credentials. Please try again later or contact
                support if this keeps happening.
              </Typography>
              <Button variant="contained" onClick={handleClose}>
                Close
              </Button>
            </Stack>
          ) : tokenState.isLoading || (!bearerToken && !tokenState.isError) ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          ) : tokenState.isError ? (
            <Stack spacing={1.5} py={4} alignItems="center">
              <Typography variant="body2" color="error.main">
                We couldn't start the tax form right now. Please try again.
              </Typography>
              <Button variant="contained" onClick={() => void fetchToken()}>
                Retry
              </Button>
            </Stack>
          ) : (
            bearerToken && (
              <TaxbitQuestionnaire
                // Key on the token so a re-mint forces a full remount; prop swaps retain
                // stale state (cached 401s).
                key={bearerToken}
                questionnaire="W-FORM"
                bearerToken={bearerToken}
                data={prefill}
                adaptiveMode={adaptiveMode}
                realTimeTinValidation
                onSuccess={() => void handleSuccess()}
                onError={(error) => void handleSdkError(error)}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TaxDocumentationWidget;
